import * as React from 'react';
import { Button, Form, Input, Modal, Select, Switch } from 'antd';
import { CloseOutlined } from '@ant-design/icons';

export interface IApiAccessUserFormValues {
    username: string;
    enabled: boolean;
    principalType: string;
    roles: string[];
}

interface IProps {
    mode: 'create' | 'edit';
    open: boolean;
    loading?: boolean;
    roleOptions: Array<{ label: string; value: string }>;
    initialValues?: Partial<IApiAccessUserFormValues>;
    onCancel: () => void;
    onSubmit: (values: IApiAccessUserFormValues) => void;
}

const PRINCIPAL_TYPE_OPTIONS = [
    { label: 'THIRD_PARTY', value: 'THIRD_PARTY' },
    { label: 'INDIVIDUAL', value: 'INDIVIDUAL' },
    { label: 'INTERNAL', value: 'INTERNAL' },
    { label: 'PARTNER', value: 'PARTNER' },
];

const DEFAULT_VALUES: IApiAccessUserFormValues = {
    username: '',
    enabled: false,
    principalType: 'THIRD_PARTY',
    roles: [],
};

const ApiAccessUserModal: React.FC<IProps> = ({
    mode,
    open,
    loading,
    roleOptions,
    initialValues,
    onCancel,
    onSubmit,
}) => {
    const [form] = Form.useForm<IApiAccessUserFormValues>();
    const isCreateMode = mode === 'create';

    React.useEffect(() => {
        if (open) {
            form.setFieldsValue({
                ...DEFAULT_VALUES,
                ...initialValues,
            });
        } else {
            form.resetFields();
        }
    }, [form, initialValues, open]);

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            footer={null}
            destroyOnClose
            centered
            width={856}
            closeIcon={<CloseOutlined className="text-gray-400" />}
            className="top-6"
            classNames={{
                content: '!rounded-2xl !p-0',
                header: '!hidden',
                body: '!p-0',
            }}
        >
            <div className="px-7 pb-6 pt-5">
                <div className="mb-1 text-xs uppercase tracking-[0.12em] text-gray-400">
                    API Access
                </div>
                <div className="mb-5 text-[22px] font-semibold text-gray-900">
                    {isCreateMode ? 'Create API User' : 'Edit API User'}
                </div>
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{ ...DEFAULT_VALUES, ...initialValues }}
                    onFinish={onSubmit}
                    className="[&_.ant-form-item]:!mb-5 [&_.ant-form-item-label>label]:!text-sm [&_.ant-form-item-label>label]:!font-medium [&_.ant-form-item-label>label]:!text-gray-900 [&_.ant-input]:!h-10 [&_.ant-input]:!rounded-xl [&_.ant-input]:!border-gray-200 [&_.ant-select-selector]:!h-10 [&_.ant-select-selector]:!rounded-xl [&_.ant-select-selector]:!border-gray-200 [&_.ant-select-selector]:!px-3 [&_.ant-select-multiple_.ant-select-selector]:!min-h-10 [&_.ant-select-multiple_.ant-select-selector]:!h-auto [&_.ant-select-multiple_.ant-select-selector]:!py-1"
                >
                    <div className="rounded-2xl border border-gray-200 bg-[#fcfcfd] p-5">
                        <div className={`grid gap-4 ${isCreateMode ? 'grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)]' : 'grid-cols-1'}`}>
                            <Form.Item
                                label="Username"
                                name="username"
                                rules={[{ required: true, message: 'Username is required' }]}
                            >
                                <Input placeholder="Username" />
                            </Form.Item>
                        </div>

                        <Form.Item label="Status" className="!mb-5">
                            <div className="flex min-h-10 items-center justify-between rounded-xl border border-gray-200 bg-white px-3">
                                <span>Enabled</span>
                                <Form.Item name="enabled" valuePropName="checked" noStyle>
                                    <Switch />
                                </Form.Item>
                            </div>
                        </Form.Item>

                        <Form.Item
                            label="Principal type"
                            name="principalType"
                            rules={[{ required: true, message: 'Principal type is required' }]}
                        >
                            <Select options={PRINCIPAL_TYPE_OPTIONS} disabled={!isCreateMode} />
                        </Form.Item>

                        <Form.Item
                            label="Role"
                            name="roles"
                            rules={[{ required: true, message: 'At least one role is required' }]}
                            className="!mb-0"
                        >
                            <Select
                                mode="multiple"
                                placeholder="Select Role"
                                options={roleOptions}
                                maxTagCount="responsive"
                            />
                        </Form.Item>
                    </div>

                    <div className="mt-5 flex flex-col-reverse justify-end gap-3 md:flex-row">
                        <Button shape="round" onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            shape="round"
                            loading={loading}
                            className="!border-0 !bg-[#df1f26] !shadow-none hover:!bg-[#c91b21] focus:!bg-[#c91b21]"
                        >
                            {isCreateMode ? 'Create API User' : 'Save Changes'}
                        </Button>
                    </div>
                </Form>
            </div>
        </Modal>
    );
};

export default ApiAccessUserModal;
